/**
 * Studio Booking · Manual booking dialog + TimeSelect30 — extracted Oct 2026.
 * Used by both the Bookings table (admin manual-add) and the Shoots tab.
 * No cross-module state leaks out of this file.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import SearchableSelect from "@/components/ui/SearchableSelect";

export function TimeSelect30({ value, onChange, testid }) {
  const options = [];
  for (let h = 6; h <= 22; h++) for (const m of [0, 30]) options.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
  options.push("23:00");
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}
              data-testid={testid}
              className="w-full h-9 border border-border bg-background text-xs rounded-none px-2">
      {options.map((t) => <option key={t} value={t}>{t}</option>)}
    </select>
  );
}

export function ManualBookingDialog({ ws, open, onClose, onDone }) {
  const [spaces, setSpaces] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [members, setMembers] = useState([]);
  const [avail, setAvail] = useState(null);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ space_id: "", customer_name: "Internal", customer_email: "",
      customer_phone: "", client_id: "", project_id: "",
      slot_start_date: today, slot_start_time: "10:00",
      slot_end_date: today, slot_end_time: "18:00",
      equipment_item_ids: [], crew_user_ids: [],
      promo_code: "",
      allow_off_hours: false, internal_reason: "" });
  const [busy, setBusy] = useState(false);
  // Feb 2026 · Live quote so admins see the promo discount as they type
  const [quote, setQuote] = useState(null);
  const [promoStatus, setPromoStatus] = useState(null);
  useEffect(() => {
    if (!open || !ws) return;
    api.get(`/workspaces/${ws}/studio/spaces`).then((r) => setSpaces(r.data.spaces || [])).catch(() => {});
    api.get(`/workspaces/${ws}/clients`).then((r) => setClients(r.data || [])).catch(() => setClients([]));
    api.get(`/workspaces/${ws}/studio/equipment-items`).then((r) => setEquipment(r.data["equipment-items"] || r.data.items || r.data.equipment_items || [])).catch(() => setEquipment([]));
    api.get(`/workspaces/${ws}/members`).then((r) => setMembers(Array.isArray(r.data) ? r.data : [])).catch(() => setMembers([]));
  }, [open, ws]);
  useEffect(() => {
    if (!form.client_id) { setProjects([]); return; }
    api.get(`/clients/${form.client_id}/projects`).then((r) => setProjects(r.data || []))
       .catch(() => setProjects([]));
  }, [form.client_id]);
  // Feb 2026 · Live availability probe so admins see off-hours + conflicts
  // BEFORE they hit Create, and can flip the override checkbox knowingly.
  useEffect(() => {
    if (!open || !ws || !form.space_id) { setAvail(null); return; }
    let cancelled = false;
    api.post(`/workspaces/${ws}/studio/check-availability`, {
      space_id: form.space_id,
      slot_start: `${form.slot_start_date}T${form.slot_start_time}:00`,
      slot_end:   `${form.slot_end_date}T${form.slot_end_time}:00`,
      equipment_item_ids: form.equipment_item_ids,
    }).then((r) => { if (!cancelled) setAvail(r.data); })
      .catch(() => { if (!cancelled) setAvail(null); });
    return () => { cancelled = true; };
  }, [open, ws, form.space_id, form.slot_start_date, form.slot_start_time, form.slot_end_date, form.slot_end_time, form.equipment_item_ids]);

  // Feb 2026 · Live-price the manual booking so admins see the promo
  // discount immediately. Runs whenever slot/equipment/promo changes.
  useEffect(() => {
    if (!open || !ws || !form.space_id) { setQuote(null); return; }
    let cancelled = false;
    api.post(`/workspaces/${ws}/studio/quote`, {
      space_id: form.space_id,
      slot_start: `${form.slot_start_date}T${form.slot_start_time}:00`,
      slot_end:   `${form.slot_end_date}T${form.slot_end_time}:00`,
      equipment_item_ids: form.equipment_item_ids,
      promo_code: form.promo_code || null,
    }).then((r) => {
      if (cancelled) return;
      setQuote(r.data);
      if (form.promo_code) {
        setPromoStatus(r.data?.promo ? "valid" : "invalid");
      } else {
        setPromoStatus(null);
      }
    }).catch(() => { if (!cancelled) { setQuote(null); setPromoStatus(null); } });
    return () => { cancelled = true; };
  }, [open, ws, form.space_id, form.slot_start_date, form.slot_start_time, form.slot_end_date, form.slot_end_time, form.equipment_item_ids, form.promo_code]);

  const submit = async () => {
    if (!form.space_id) { toast.error("Pick a space"); return; }
    setBusy(true);
    try {
      const body = {
        space_id: form.space_id,
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        client_id: form.client_id || null,
        project_id: form.project_id || null,
        slot_start: `${form.slot_start_date}T${form.slot_start_time}:00`,
        slot_end:   `${form.slot_end_date}T${form.slot_end_time}:00`,
        equipment_item_ids: form.equipment_item_ids,
        crew_user_ids: form.crew_user_ids,
        equipment: [], packages: [], crew: [],
        promo_code: form.promo_code || null,
        allow_off_hours: form.allow_off_hours,
        internal_reason: form.internal_reason,
      };
      await api.post(`/workspaces/${ws}/studio/bookings/manual`, body);
      toast.success(form.allow_off_hours
        ? "Off-hours booking created — admins notified"
        : "Manual booking created (no payment)");
      onDone && await onDone();
      onClose();
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); }
    finally { setBusy(false); }
  };

  const sortedClients = [...clients].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  const sortedProjects = [...projects].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  const offHours = (avail?.conflicts?.off_hours || []);
  const hardBlocked =
    avail && avail.available === false
    && ((avail.conflicts?.space || []).length > 0 || Object.keys(avail.conflicts?.equipment || {}).length > 0);
  const canSubmit = !!form.space_id && !busy && !hardBlocked && (offHours.length === 0 || form.allow_off_hours);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-none max-w-2xl w-[calc(100vw-1rem)] max-h-[92vh] overflow-y-auto p-0" data-testid="sb-manual-booking-dialog">
        <DialogHeader className="p-4 sm:p-5 border-b border-border sticky top-0 bg-background z-10">
          <DialogTitle className="text-base sm:text-lg">Manual booking</DialogTitle>
          <div className="text-[11px] text-muted-foreground">Internal / walk-in booking · no Cashfree payment link is generated.</div>
        </DialogHeader>
        <div className="p-4 sm:p-5 space-y-5">

          {/* Section 1 · Space + Slot */}
          <section>
            <div className="text-[10px] uppercase tracking-widest text-primary font-semibold mb-2">1 · Slot</div>
            <div className="space-y-2">
              <div>
                <Label className="text-xs">Space *</Label>
                <select value={form.space_id} onChange={(e) => setForm({ ...form, space_id: e.target.value })}
                         className="w-full h-9 border border-border bg-background text-xs rounded-none px-2"
                         data-testid="sb-manual-space">
                  <option value="">Select…</option>
                  {[...spaces].sort((a, b) => a.name.localeCompare(b.name)).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Start date</Label>
                  <Input type="date" value={form.slot_start_date} onChange={(e) => setForm({ ...form, slot_start_date: e.target.value })}
                          className="rounded-none h-9" data-testid="sb-manual-start-date" /></div>
                <div><Label className="text-xs">Start time</Label>
                  <TimeSelect30 value={form.slot_start_time} onChange={(v) => setForm({ ...form, slot_start_time: v })} testid="sb-manual-start-time" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">End date</Label>
                  <Input type="date" value={form.slot_end_date} onChange={(e) => setForm({ ...form, slot_end_date: e.target.value })}
                          className="rounded-none h-9" data-testid="sb-manual-end-date" /></div>
                <div><Label className="text-xs">End time</Label>
                  <TimeSelect30 value={form.slot_end_time} onChange={(v) => setForm({ ...form, slot_end_time: v })} testid="sb-manual-end-time" />
                </div>
              </div>
              {avail && avail.available === false && (
                <div className={`p-2.5 text-xs border ${hardBlocked ? "bg-rose-50 border-rose-300 text-rose-900" : "bg-amber-50 border-amber-300 text-amber-900"}`}
                     data-testid="sb-manual-avail-warn">
                  <div className="font-semibold mb-1">{hardBlocked ? "⛔ Selected slot is not available" : "⚠ Selected slot is not available"}</div>
                  {/* Oct 2026 · Softened the per-reason copy. Admins
                      can still inspect the raw conflicts payload if
                      they need the specific root cause. */}
                  {offHours.length > 0 && (
                    <div className="ml-2">· Pick another date or time within the studio's hours.</div>
                  )}
                  {(avail.conflicts?.space || []).length > 0 && <div className="ml-2">· Another booking covers this time (including the clean-up buffer).</div>}
                  {Object.keys(avail.conflicts?.equipment || {}).length > 0 && <div className="ml-2">· Equipment reserved · {Object.keys(avail.conflicts.equipment).length} item(s)</div>}
                  {(avail.conflicts?.equipment_off_hours || []).length > 0 && (() => {
                    const names = Array.from(new Set((avail.conflicts.equipment_off_hours || []).map((o) => o.equipment_name).filter(Boolean)));
                    return (
                      <div className="ml-2">
                        · {names.length ? `${names.slice(0, 3).join(", ")}${names.length > 3 ? "…" : ""} not available on this slot (check equipment calendar)` : "Some equipment is not available on this slot"}
                      </div>
                    );
                  })()}
                  {avail.conflicts?.buffer_min > 0 && (
                    <div className="ml-2 text-[11px] opacity-75">
                      Clean-up buffer: {avail.conflicts.buffer_min} min
                    </div>
                  )}
                </div>
              )}
              {offHours.length > 0 && !hardBlocked && (
                <label className="flex items-start gap-2 p-2 border border-amber-300 bg-amber-50 text-xs cursor-pointer"
                        data-testid="sb-manual-off-hours-toggle">
                  <input type="checkbox" checked={form.allow_off_hours}
                          onChange={(e) => setForm({ ...form, allow_off_hours: e.target.checked })}
                          className="mt-0.5" />
                  <span>
                    <strong>Allow off-hours override.</strong> Admins will be notified by email.
                  </span>
                </label>
              )}
            </div>
          </section>

          {/* Section 2 · Customer & Links */}
          <section>
            <div className="text-[10px] uppercase tracking-widest text-primary font-semibold mb-2">2 · Customer &amp; project</div>
            <div className="space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div><Label className="text-xs">Customer name</Label>
                  <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                          className="rounded-none h-9" data-testid="sb-manual-name" /></div>
                <div><Label className="text-xs">Phone (opt.)</Label>
                  <Input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                          className="rounded-none h-9" data-testid="sb-manual-phone" /></div>
              </div>
              <div><Label className="text-xs">Email (opt.)</Label>
                <Input value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                        className="rounded-none h-9" data-testid="sb-manual-email" /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Link client (opt.)</Label>
                  <SearchableSelect
                    value={form.client_id}
                    onValueChange={(v) => setForm({ ...form, client_id: v || "", project_id: "" })}
                    options={sortedClients.map((c) => ({ value: c.id, label: c.name }))}
                    placeholder="Search clients…"
                    clearable
                    triggerClassName="h-9 text-xs w-full"
                    width="100%"
                    data-testid="sb-manual-client"
                  />
                </div>
                <div>
                  <Label className="text-xs">Link project (opt.)</Label>
                  <SearchableSelect
                    value={form.project_id}
                    onValueChange={(v) => setForm({ ...form, project_id: v || "" })}
                    options={sortedProjects.map((p) => ({ value: p.id, label: p.name }))}
                    placeholder={form.client_id ? "Search projects…" : "Pick client first"}
                    disabled={!form.client_id}
                    clearable
                    triggerClassName="h-9 text-xs w-full"
                    width="100%"
                    data-testid="sb-manual-project"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Section 3 · Equipment */}
          <section>
            <div className="text-[10px] uppercase tracking-widest text-primary font-semibold mb-2">3 · Equipment to block <span className="text-muted-foreground font-normal normal-case ml-1">(optional)</span></div>
            {equipment.length === 0 ? (
              <div className="text-[11px] text-muted-foreground italic">No equipment inventory in this workspace yet.</div>
            ) : (
              <div className="border border-border max-h-40 overflow-y-auto" data-testid="sb-manual-equipment-list">
                {[...equipment].sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((eq) => {
                  const on = form.equipment_item_ids.includes(eq.id);
                  const conflict = !!(avail?.conflicts?.equipment || {})[eq.id];
                  return (
                    <label key={eq.id}
                            className={`flex items-center gap-2 px-2 py-1 border-b border-border last:border-b-0 text-xs cursor-pointer hover:bg-muted/30 ${conflict ? "bg-rose-50" : ""}`}
                            data-testid={`sb-manual-equipment-row-${eq.id}`}>
                      <input type="checkbox" checked={on}
                              onChange={(e) => setForm({ ...form,
                                equipment_item_ids: e.target.checked
                                  ? [...form.equipment_item_ids, eq.id]
                                  : form.equipment_item_ids.filter((x) => x !== eq.id) })} />
                      <span className="flex-1 truncate">{eq.name}</span>
                      {conflict && <span className="text-[9px] text-rose-700 font-semibold">RESERVED</span>}
                      <span className="text-[10px] text-muted-foreground">₹{eq.price_per_hour || 0}/hr</span>
                    </label>
                  );
                })}
              </div>
            )}
          </section>

          {/* Section 4 · Crew — creates a user task in the linked project */}
          <section>
            <div className="text-[10px] uppercase tracking-widest text-primary font-semibold mb-2">4 · Crew to block <span className="text-muted-foreground font-normal normal-case ml-1">(creates a task in their calendar)</span></div>
            {members.length === 0 ? (
              <div className="text-[11px] text-muted-foreground italic">Loading workspace members…</div>
            ) : (
              <div className="border border-border max-h-40 overflow-y-auto" data-testid="sb-manual-crew-list">
                {[...members].sort((a, b) => (a.name || a.email || "").localeCompare(b.name || b.email || "")).map((m) => {
                  const uid = m.user_id || m.id;
                  const on = form.crew_user_ids.includes(uid);
                  return (
                    <label key={uid}
                            className="flex items-center gap-2 px-2 py-1 border-b border-border last:border-b-0 text-xs cursor-pointer hover:bg-muted/30"
                            data-testid={`sb-manual-crew-row-${uid}`}>
                      <input type="checkbox" checked={on}
                              onChange={(e) => setForm({ ...form,
                                crew_user_ids: e.target.checked
                                  ? [...form.crew_user_ids, uid]
                                  : form.crew_user_ids.filter((x) => x !== uid) })} />
                      <span className="flex-1 truncate">{m.name || m.email || uid.slice(0, 8)}</span>
                      {m.email && m.name && <span className="text-[10px] text-muted-foreground hidden sm:inline">{m.email}</span>}
                    </label>
                  );
                })}
              </div>
            )}
            {form.crew_user_ids.length > 0 && !form.client_id && (
              <div className="mt-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-300 p-2">
                ℹ No client/project linked — crew tasks will be created without a project. Link one above to keep the shoot on that project&apos;s task board.
              </div>
            )}
          </section>

          {/* Section 5 · Promo / discount */}
          <section>
            <div className="text-[10px] uppercase tracking-widest text-primary font-semibold mb-2">
              5 · Promo code <span className="text-muted-foreground font-normal normal-case ml-1">(optional)</span>
            </div>
            <div className="flex gap-2 items-start">
              <div className="flex-1">
                <Input value={form.promo_code}
                       onChange={(e) => setForm({ ...form, promo_code: e.target.value.toUpperCase() })}
                       placeholder="LAUNCH20"
                       className="rounded-none h-9 font-mono uppercase"
                       data-testid="sb-manual-promo" />
                {form.promo_code && promoStatus && (
                  <div className={`text-[11px] mt-1 ${promoStatus === "valid" ? "text-emerald-700" : "text-rose-600"}`}
                       data-testid="sb-manual-promo-msg">
                    {promoStatus === "valid"
                      ? `✓ Applied — saved ₹${quote?.promo?.discount?.toFixed(2)}`
                      : "⚠ Code not valid for this booking"}
                  </div>
                )}
              </div>
              {quote && (
                <div className="text-right text-xs bg-muted/40 p-2 border border-border min-w-[140px]"
                     data-testid="sb-manual-quote-summary">
                  <div className="text-[10px] text-muted-foreground uppercase tracking-widest">Live total</div>
                  <div className="font-mono font-bold text-sm">₹{quote.total?.toFixed(2)}</div>
                  {quote.promo && (
                    <div className="text-[10px] text-emerald-700 font-semibold">-₹{quote.promo.discount?.toFixed(2)}</div>
                  )}
                </div>
              )}
            </div>
          </section>

          {/* Section 6 · Reason */}
          <section>
            <div className="text-[10px] uppercase tracking-widest text-primary font-semibold mb-2">6 · Internal note</div>
            <Textarea value={form.internal_reason} onChange={(e) => setForm({ ...form, internal_reason: e.target.value })}
                       rows={2} className="rounded-none text-xs"
                       placeholder="Product test shoot · staff portfolio · maintenance hold…"
                       data-testid="sb-manual-reason" />
          </section>
        </div>
        <DialogFooter className="p-4 sm:p-5 border-t border-border sticky bottom-0 bg-background z-10 flex-col sm:flex-row gap-2">
          <div className="text-[11px] text-muted-foreground sm:mr-auto text-left">
            {form.equipment_item_ids.length > 0 && `${form.equipment_item_ids.length} equipment `}
            {form.crew_user_ids.length > 0 && ` · ${form.crew_user_ids.length} crew`}
          </div>
          <div className="flex gap-2 justify-end w-full sm:w-auto">
            <Button variant="outline" className="rounded-none flex-1 sm:flex-initial" onClick={onClose}>Cancel</Button>
            <Button className="rounded-none flex-1 sm:flex-initial" onClick={submit} disabled={!canSubmit} data-testid="sb-manual-save">
              {busy ? "Creating…"
                : form.allow_off_hours ? "Create off-hours booking"
                : "Create booking"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
